/**
 * Unified Inbox Type Definitions
 *
 * Comprehensive TypeScript interfaces for the Unified Inbox feature that consolidates
 * conversation and ticket management into a single, modern UI.
 */

// Re-export existing types from chat.types.ts
export type {
  // Entities
  Channel,
  ChannelConnection,
  Contact,
  ContactIdentity,
  Conversation,
  ConversationParticipant,
  Message,
  MessageAttachment,
  MessageReaction,
  Ticket,
  TicketQueue,
  TagAssignment,
  Tag,
  User,
  // WebSocket types
  WsConnectionAuth,
  WsSubscribePayload,
  WsMessagePayload,
  WsTypingPayload,
  WsTicketPayload,
  WsReactionPayload,
  WsParticipantPayload,
  WsReadReceiptPayload,
  WsSubscribedPayload,
  RaRealtimeEvent,
  AccessLevel,
} from '../../../types/chat.types';

export {
  // Enums
  ChannelType,
  TicketStatus,
  TicketPriority,
  MessageType,
  MessageStatus,
  ParticipantRole,
  ConversationSource,
  ConversationType,
  ConversationVisibility,
} from '../../../types/chat.types';

import type {
  Conversation,
  Message,
  Ticket,
  Contact,
  User,
  TicketStatus,
  TicketPriority,
  MessageAttachment,
} from '../../../types/chat.types';

// ============================================
// FILTER TYPES
// ============================================

/**
 * Filter options for conversation list (tabs)
 * Reduced to 2 tabs: All and Mine
 * UNASSIGNED moved to filter options
 */
export enum ConversationFilter {
  ALL = 'all',
  MINE = 'mine',
}

/**
 * Assignment filter options (replaces UNASSIGNED tab)
 */
export type AssignmentFilter = 'all' | 'mine' | 'unassigned';

/**
 * Ticket tab filter - assignment-based views
 */
export enum TicketTabFilter {
  MINE = 'mine',
  UNASSIGNED = 'unassigned',
  ALL = 'all',
}

/**
 * Ticket status for list display
 */
export type TicketListStatus = 'open' | 'pending' | 'closed';

/**
 * Queue interface for ticket queue selection
 */
export interface TicketQueueOption {
  id: number;
  uuid: string;
  name: string;
  color?: string;
}

/**
 * Options for filtering and sorting conversation list
 */
export interface ConversationListOptions {
  /** Active filter tab */
  filter: ConversationFilter;
  /** Search query for contact name and last message */
  search?: string;
  /** Filter by ticket status */
  status?: TicketStatus | null;
  /** Filter by ticket priority */
  priority?: TicketPriority | null;
  /** Filter by queue UUID */
  queueUuid?: string | null;
  /** Filter by channel UUID */
  channelUuid?: string | null;
  /** Filter by assigned user ID */
  assignedUserId?: number | null;
  /** Show only conversations with unread messages */
  unreadOnly?: boolean;
  /** Page number for pagination */
  page?: number;
  /** Number of items per page */
  perPage?: number;
  /** Sort field */
  sortBy?: 'lastActivityAt' | 'createdAt' | 'priority';
  /** Sort direction */
  sortOrder?: 'asc' | 'desc';
  /** Assignment filter (replaces UNASSIGNED tab) */
  assignment?: AssignmentFilter;
}

/**
 * Current state of active filters
 */
export interface FilterState {
  /** Currently selected filter tab */
  activeFilter: ConversationFilter;
  /** Applied filter options */
  options: ConversationListOptions;
  /** Whether filters panel is expanded (mobile) */
  isExpanded: boolean;
}

// ============================================
// UI STATE TYPES
// ============================================

/**
 * Visibility state for the three panels
 */
export interface PanelVisibility {
  /** Show conversation list panel */
  showLeft: boolean;
  /** Show chat panel */
  showCenter: boolean;
  /** Show details panel */
  showRight: boolean;
}

/**
 * Layout breakpoint indicators
 */
export enum LayoutBreakpoint {
  MOBILE = 'mobile',        // < 900px
  TABLET = 'tablet',        // 900px - 1200px
  DESKTOP = 'desktop',      // >= 1200px
}

/**
 * Typing indicator for a user
 */
export interface TypingUser {
  /** User ID */
  userId: number;
  /** User email or display name */
  userEmail?: string;
  /** User name */
  userName?: string;
  /** Timestamp when typing started */
  timestamp: number;
}

/**
 * Map of conversation IDs to unread message counts
 */
export type UnreadCountMap = Record<number, number>;

/**
 * Map of conversation IDs to arrays of typing users
 */
export type TypingUsersMap = Record<number, TypingUser[]>;

/**
 * Quick action button state
 */
export interface QuickAction {
  /** Unique action identifier */
  id: string;
  /** Display label */
  label: string;
  /** Icon name or component */
  icon: string;
  /** Click handler */
  onClick: () => void;
  /** Whether action is disabled */
  disabled?: boolean;
  /** Variant for button styling */
  variant?: 'text' | 'outlined' | 'contained';
  /** Color theme */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /** Loading state */
  loading?: boolean;
}

// ============================================
// COMPONENT PROPS
// ============================================

/**
 * Props for ConversationListPanel component
 * Note: Panel manages its own filter state internally via useConversationList hook
 */
export interface ConversationListPanelProps {
  /** ID of currently selected conversation */
  selectedConversationId: number | null;
  /** Callback when a conversation is selected */
  onConversationSelect: (conversation: Conversation) => void;
  /** Whether panel is visible (mobile) */
  isVisible?: boolean;
  /** Callback to close panel (mobile) */
  onClose?: () => void;
  /** Fixed width for the panel (responsive by default) */
  width?: number | string;
  /** Custom class name */
  className?: string;
}

/**
 * Props for ConversationListItem component
 */
export interface ConversationListItemProps {
  /** Conversation data */
  conversation: Conversation;
  /** Whether this item is selected */
  isSelected: boolean;
  /** Callback when item is clicked */
  onClick: (conversation: Conversation) => void;
  /** Number of unread messages */
  unreadCount?: number;
  /** Users currently typing in this conversation */
  typingUsers?: TypingUser[];
  /** Whether to show ticket badge */
  showTicketBadge?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for ConversationFilters component
 */
export interface ConversationFiltersProps {
  /** Current filter state */
  filterState: FilterState;
  /** Callback when filter options change */
  onFilterChange: (options: Partial<ConversationListOptions>) => void;
  /** Callback when filter tab changes */
  onTabChange: (filter: ConversationFilter) => void;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Callback when sort field changes */
  onSortChange?: (sortBy: 'lastActivityAt' | 'createdAt') => void;
  /** Current sort field */
  sortBy?: 'lastActivityAt' | 'createdAt';
  /** Available queues for filtering */
  availableQueues?: Array<{ uuid: string; name: string }>;
  /** Available channels for filtering */
  availableChannels?: Array<{ uuid: string; name: string }>;
  /** Whether filters are collapsed (mobile) */
  isCollapsed?: boolean;
  /** Callback to toggle collapse state */
  onToggleCollapse?: () => void;
  /** Custom class name */
  className?: string;
}

// ============================================
// TICKETZ-STYLE COMPONENT PROPS
// ============================================

/**
 * Props for TicketListItem component (ticketz-style)
 */
export interface TicketListItemProps {
  /** Ticket data with conversation info */
  ticket: Ticket & {
    conversation?: Conversation;
    contact?: Contact;
    queue?: { id: number; name: string; color?: string };
  };
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Callback for accepting a pending ticket */
  onAccept?: (ticket: Ticket) => Promise<void>;
  /** Callback when ticket is clicked */
  onClick?: (ticket: Ticket) => void;
  /** Callback for right-click context menu */
  onContextMenu?: (event: React.MouseEvent, ticket: Ticket) => void;
  /** Callback to spy on ticket */
  onSpy?: (ticket: Ticket) => void;
  /** Whether to show group action buttons (for group chats) */
  groupActionButtons?: boolean;
  /** Current active tab (used to determine if status tooltip is redundant) */
  activeTab?: TicketTabFilter;
}

/**
 * Props for TicketsList component (ticketz-style)
 */
export interface TicketsListProps {
  /** Active tab filter */
  tab: TicketTabFilter;
  /** Search query for filtering (persistent, not tab-bound) */
  searchParam?: string;
  /** Status filter (dropdown) */
  statusFilter?: TicketStatus | null;
  /** Priority filter */
  priorityFilter?: TicketPriority | null;
  /** Selected queue IDs for filtering */
  selectedQueueIds?: number[];
  /** Channel filter */
  channelFilter?: string | null;
  /** Sort field */
  sortField?: 'updatedAt' | 'createdAt' | 'priority';
  /** Sort order */
  sortOrder?: 'ASC' | 'DESC';
  /** Callback when ticket is selected */
  onTicketSelect?: (ticket: Ticket) => void;
  /** Currently selected ticket ID */
  selectedTicketId?: number | null;
  /** Callback for right-click context menu */
  onContextMenu?: (event: React.MouseEvent, ticket: Ticket) => void;
}

/**
 * Props for TicketsManager component (ticketz-style)
 */
export interface TicketsManagerProps {
  /** Callback when ticket is selected */
  onTicketSelect?: (ticket: Ticket) => void;
  /** Currently selected ticket ID */
  selectedTicketId?: number | null;
}

/**
 * Props for ChatPanel component
 */
export interface ChatPanelProps {
  /** Current conversation */
  conversation: Conversation | null;
  /** Current ticket (if any) */
  ticket: Ticket | null;
  /** Messages to display */
  messages: Message[];
  /** Users currently typing */
  typingUsers: TypingUser[];
  /** Loading state for messages */
  isLoading: boolean;
  /** Whether more messages are available */
  hasMore: boolean;
  /** Callback to load more messages */
  onLoadMore?: () => void;
  /** Callback to send a message */
  onSendMessage: (body: string, attachments?: MessageAttachment[]) => Promise<void>;
  /** Callback when user starts typing */
  onTyping: () => void;
  /** Callback when user stops typing */
  onStopTyping: () => void;
  /** Callback to create a ticket from this conversation */
  onCreateTicket?: () => void;
  /** Callback to toggle details panel */
  onToggleDetails?: () => void;
  /** Whether details panel is visible */
  showDetails?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for ChatHeader component
 */
export interface ChatHeaderProps {
  /** Current conversation */
  conversation: Conversation | null;
  /** Current ticket (if any) */
  ticket: Ticket | null;
  /** Callback to create a ticket */
  onCreateTicket?: () => void;
  /** Callback to toggle details panel */
  onToggleDetails?: () => void;
  /** Whether details panel is visible */
  showDetails?: boolean;
  /** Callback to go back (mobile) */
  onBack?: () => void;
  /** Custom quick actions */
  quickActions?: QuickAction[];
  /** Custom class name */
  className?: string;
}

/**
 * Props for MessageThread component
 */
export interface MessageThreadProps {
  /** Messages to display */
  messages: Message[];
  /** Loading state */
  isLoading: boolean;
  /** Whether more messages are available */
  hasMore: boolean;
  /** Callback to load more messages */
  onLoadMore?: () => void;
  /** Current user ID for message positioning */
  currentUserId: number;
  /** Users currently typing */
  typingUsers?: TypingUser[];
  /** Callback when a message is clicked */
  onMessageClick?: (message: Message) => void;
  /** Enable message virtualization for performance */
  enableVirtualization?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for MessageComposer component
 */
export interface MessageComposerProps {
  /** Callback to send a message */
  onSendMessage: (body: string, attachments?: MessageAttachment[]) => Promise<void>;
  /** Callback when user starts typing */
  onTyping?: () => void;
  /** Callback when user stops typing */
  onStopTyping?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether composer is disabled */
  disabled?: boolean;
  /** Whether to show emoji picker */
  showEmojiPicker?: boolean;
  /** Whether to show attachment button */
  showAttachments?: boolean;
  /** Whether to show quick replies */
  showQuickReplies?: boolean;
  /** Available quick reply templates */
  quickReplies?: Array<{ id: string; text: string }>;
  /** Maximum message length */
  maxLength?: number;
  /** Auto-focus on mount */
  autoFocus?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for DetailsPanel component
 */
export interface DetailsPanelProps {
  /** Current conversation */
  conversation: Conversation | null;
  /** Current ticket (if any) */
  ticket: Ticket | null;
  /** Callback to update ticket */
  onTicketUpdate: (data: Partial<Ticket>) => Promise<void>;
  /** Callback to create a new ticket */
  onCreateTicket: () => void;
  /** Callback to close panel (mobile) */
  onClose?: () => void;
  /** Whether panel is visible */
  isVisible: boolean;
  /** Custom quick actions */
  quickActions?: QuickAction[];
  /** Custom class name */
  className?: string;
}

/**
 * Props for ContactDetails component
 */
export interface ContactDetailsProps {
  /** Contact information */
  contact: Contact | null;
  /** Current conversation */
  conversation: Conversation | null;
  /** Previous conversations with this contact */
  previousConversations?: Conversation[];
  /** Callback to navigate to a previous conversation */
  onConversationClick?: (conversation: Conversation) => void;
  /** Loading state */
  isLoading?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for TicketDetails component
 */
export interface TicketDetailsProps {
  /** Ticket information */
  ticket: Ticket | null;
  /** Whether ticket can be edited */
  canEdit?: boolean;
  /** Callback to open edit dialog */
  onEdit?: () => void;
  /** Callback to refresh ticket data */
  onRefresh?: () => void;
  /** Loading state */
  isLoading?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Props for TicketActions component
 */
export interface TicketActionsProps {
  /** Current ticket */
  ticket: Ticket | null;
  /** Whether actions are disabled */
  disabled?: boolean;
  /** Callback to assign ticket */
  onAssign?: () => void;
  /** Callback to transfer ticket */
  onTransfer?: () => void;
  /** Callback to resolve ticket */
  onResolve?: () => void;
  /** Callback to close ticket */
  onClose?: () => void;
  /** Callback to reopen ticket */
  onReopen?: () => void;
  /** Custom actions */
  customActions?: QuickAction[];
  /** Orientation of action buttons */
  orientation?: 'horizontal' | 'vertical';
  /** Custom class name */
  className?: string;
}

// ============================================
// HOOK RETURN TYPES
// ============================================

/**
 * Return type for useConversationList hook
 * Manages conversation list with filtering and real-time updates
 */
export interface UseConversationListReturn {
  // Data
  /** Filtered and sorted conversations */
  conversations: Conversation[];
  /** Total count of conversations matching filters */
  totalCount: number;
  /** Current filter state */
  filterState: FilterState;
  /** Map of unread counts by conversation UUID */
  unreadCountMap: UnreadCountMap;
  /** Map of typing users by conversation UUID */
  typingUsersMap: TypingUsersMap;
  /** Loading state */
  isLoading: boolean;
  /** Whether more conversations are available for infinite scroll */
  hasMore: boolean;
  /** Error state */
  error: Error | null;

  // Actions
  /** Update filter options */
  setFilterOptions: (options: Partial<ConversationListOptions>) => void;
  /** Change active filter tab */
  setActiveFilter: (filter: ConversationFilter) => void;
  /** Update search query */
  setSearchQuery: (query: string) => void;
  /** Change sort field */
  setSortBy: (sortBy: 'lastActivityAt' | 'createdAt') => void;
  /** Load more conversations (pagination) */
  loadMore: () => void;
  /** Refresh conversation list */
  refresh: () => void;
  /** Mark conversation as read */
  markAsRead: (conversationId: number) => Promise<void>;
}

/**
 * Return type for useMessageThread hook
 * Manages message loading and real-time updates
 */
export interface UseMessageThreadReturn {
  // Data
  /** Messages for current conversation */
  messages: Message[];
  /** Users currently typing */
  typingUsers: TypingUser[];
  /** Whether more messages are available */
  hasMore: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Sending state */
  isSending: boolean;
  /** Error state */
  error: Error | null;

  // Actions
  /** Load more messages (pagination) */
  loadMore: () => void;
  /** Send a new message */
  sendMessage: (body: string, attachments?: MessageAttachment[]) => Promise<void>;
  /** Emit typing indicator */
  emitTyping: () => void;
  /** Stop typing indicator */
  emitStopTyping: () => void;
  /** Refresh messages */
  refresh: () => void;
  /** Mark messages as read */
  markAsRead: () => Promise<void>;
}

/**
 * Return type for useTicketActions hook
 * Manages ticket mutations and actions
 */
export interface UseTicketActionsReturn {
  // State
  /** Loading state for actions */
  isLoading: boolean;
  /** Error state */
  error: Error | null;

  // Actions
  /** Create a new ticket for conversation */
  createTicket: (conversationUuid: string, data: CreateTicketData) => Promise<Ticket>;
  /** Update existing ticket */
  updateTicket: (ticketUuid: string, data: Partial<Ticket>) => Promise<Ticket>;
  /** Assign ticket to a user */
  assignTicket: (ticketUuid: string, userId: number) => Promise<Ticket>;
  /** Transfer ticket to another queue */
  transferTicket: (ticketUuid: string, queueUuid: string) => Promise<Ticket>;
  /** Resolve ticket */
  resolveTicket: (ticketUuid: string) => Promise<Ticket>;
  /** Close ticket */
  closeTicket: (ticketUuid: string) => Promise<Ticket>;
  /** Reopen ticket */
  reopenTicket: (ticketUuid: string) => Promise<Ticket>;
  /** Update ticket priority */
  updatePriority: (ticketUuid: string, priority: TicketPriority) => Promise<Ticket>;
}

// ============================================
// FORM DATA TYPES
// ============================================

/**
 * Data for creating a new ticket
 */
export interface CreateTicketData {
  /** Conversation UUID */
  conversationUuid: string;
  /** Ticket priority */
  priority?: TicketPriority;
  /** Queue UUID */
  queueUuid?: string;
  /** Assigned user ID */
  assignedUserId?: number;
  /** Start message ID */
  startMessageId?: number;
}

/**
 * Data for creating a new conversation
 */
export interface CreateConversationData {
  /** Contact ID or UUID */
  contactId?: number;
  contactUuid?: string;
  /** Channel connection UUID */
  channelConnectionUuid: string;
  /** Initial message body */
  initialMessage?: string;
  /** Whether to auto-create ticket */
  autoCreateTicket?: boolean;
}

/**
 * Data for transferring a ticket
 */
export interface TransferTicketData {
  /** Target queue UUID */
  queueUuid: string;
  /** Optional note about transfer */
  note?: string;
  /** Whether to keep current assignee */
  keepAssignee?: boolean;
}

/**
 * Data for assigning a ticket
 */
export interface AssignTicketData {
  /** User ID to assign to */
  userId: number;
  /** Optional note about assignment */
  note?: string;
}

// ============================================
// UTILITY TYPES
// ============================================

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  /** Current page (1-indexed) */
  page: number;
  /** Items per page */
  perPage: number;
  /** Total number of items */
  total: number;
  /** Total number of pages */
  totalPages: number;
  /** Whether there are more pages */
  hasMore: boolean;
}

/**
 * API response wrapper with pagination
 */
export interface PaginatedResponse<T> {
  /** Response data */
  data: T[];
  /** Pagination metadata */
  meta: PaginationMeta;
}

/**
 * API response wrapper
 */
export interface ApiResponse<T> {
  /** Response data */
  data: T;
  /** Optional message */
  message?: string;
}

/**
 * Sort configuration
 */
export interface SortConfig {
  /** Field to sort by */
  field: string;
  /** Sort direction */
  order: 'asc' | 'desc';
}

/**
 * Attachment upload state
 */
export interface AttachmentUpload {
  /** Unique upload ID */
  id: string;
  /** File being uploaded */
  file: File;
  /** Upload progress (0-100) */
  progress: number;
  /** Upload status */
  status: 'pending' | 'uploading' | 'complete' | 'error';
  /** Error message if failed */
  error?: string;
  /** Uploaded attachment data */
  attachment?: MessageAttachment;
}
